// @flow
import React, {Fragment} from 'react';
import {connect} from 'react-redux';
import cs from 'classnames';

import {gt} from 'app/utils/i18n';

import {
  loadProcedureByHashid,
  loadProcedureVersionsByObjectid,
  type LoadByHashidInjectedProps,
  type LoadVersionsInjectedProps,
} from 'app/procedures/hocs';
import {
  withTypedRouter,
  type WithRouterProps,
} from 'app/general/withTypedRouter';
import {getRelatedVersionComments} from 'app/comments/selectors';

import {
  getContentFromProcedure,
  getProcessFromParams,
  getMemoizedProcedureOrphanSteps,
} from 'app/procedures/selectors';
import {whenVisible} from 'app/utils/page-visibility';
import {
  toggleProcedureAdvancedMode,
  setSideDiagramState,
} from 'app/procedures/actions';
import {NOTIFICATION_DEFAULT_TIMEOUT, showNotification} from 'app/toasts/slice';
import {showNoApprovedVersionsAlertBoxIfNotApproved} from 'app/general/structures/document-alerts/NoApprovedVersionsAlertBox';
import {ReviewReminderWarning} from 'app/review/components/ReviewReminderWarning';

import {keepUpdatedEnhancer} from 'app/general/keepUpdatedEnhancer';
import {
  runWhenAny,
  modelIdMatch,
  allRelatedMatch,
  type ModelSubscribedComponentPropsType,
  type ModelChangeMessageType,
} from 'app/subscriptions';
import {AttachTooltip} from 'app/general/overlays';
import {StyledCheckboxButton} from 'app/procedures/components/structures';
import {ProcedureDetailTour} from 'app/current-user/components/tour/ProcedureDetailTour';
import {ViewProcedureMetaData} from 'app/procedures/components/ViewProcedureMetaData';
import type {ProcedureType, VersionType} from 'app/procedures/types';
import type {ProcedureContentType} from 'app/tasks/types';
import type {ProcessType} from 'app/process/types';
import {ProcedureStep} from './ProcedureStep';
import {GenericDocumentActionButtons} from 'app/components/general/GenericDocumentActionButtons';
import VersionInfoBar from 'app/components/general/VersionInfoBar';
import ProcedurePageSidebar from './ProcedurePageSidebar';
import {LargeContentAlertBox} from 'app/general/structures/document-alerts/LargeContentAlertBox';
import {ListMessage} from 'app/components/general/ListMessage';
import {StyledStepsListDIV} from 'app/procedures/components/structures';
import {SPPage} from 'app/general/structures/SPPage';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {getProcedureName} from 'app/utils/fallbacks';
import {ProcedureHeaderOptionsDropdown} from 'app/procedures/components/ProcedureHeaderOptionsDropdown';
import {StepViewFieldWrapper} from 'app/procedures/components/step-form-fields/StepViewFieldWrapper';
import {ViewableChoices} from 'app/procedures/components/ViewableChoices';
import {
  ProcedureDiagramSidebar,
  ProcedureDiagramMobile,
  ToggleProcedureDiagramMobileButton,
} from 'app/procedures/components/ProcedureDiagram';
import type {CommentType, FilterContentType} from 'app/comments/types';
import {loadComments} from 'app/comments/actions';
import {
  loadAcceptance,
  setAcceptanceVersionViewed,
} from 'app/acceptance/actions';
import {CollapsedStepsState} from 'app/procedures/components/CollapsedStepsState';
import {StepFooter} from 'app/steps/components/StepFooter';
import {DocumentPresenceMembers} from 'app/presence/components/DocumentPresenceMembers';
import {ViewStepsModeTitle} from 'app/general/detailpage/ViewStepsModeTitle';
import {VideoManagerProvider} from 'app/general/VideoManagerProvider';
import type {StoreShapeType} from 'app/reducers/types';
import {getAcceptanceData, type AcceptanceProps} from 'app/acceptance/utils';
import {PendingAcceptanceInfoBar} from 'app/acceptance/components/PendingAcceptanceInfoBar';
import {SignoffRequiredInfoBar} from 'app/signoffs/components/SignoffRequiredInfoBar';
import {SignoffForm} from 'app/signoffs/components/SignoffForm';
import {isLastVersion} from 'app/version/utils';
import throttle from 'lodash/throttle';
import {isRightToLeftWrapper} from 'app/utils/rtl';
import {accountSettingsSelector} from 'app/settings/api';
import {CurrentUserSelector} from 'app/current-user/api';
import {fetchLanesForDocument} from 'app/lanes/fetch-lanes-for-doc-hoc';
import {detectPrintHOC} from 'app/utils/browser';
import {getComparedVersion} from 'app/utils/showdiff';
import {VersionDiffer} from 'app/components/VersionDiffer';
import {formatPreviousProcedureVersion} from 'app/procedures/utils';

type OwnProps = {||};

type RouteLocationType = {
  hidediff?: string,
  gid?: string,
};

type RouteParamsType = {
  versionid: string | void,
  hashid: string,
};

type RoutePropsType = {|
  showAccept?: boolean,
  showVersions?: boolean,
  showProcedures?: boolean,
  showProcesses?: boolean,
  showComments?: boolean,
  showActivity?: boolean,
  showTeams?: boolean,
  showReview?: boolean,
|};

type RouterProps = WithRouterProps<
  RouteLocationType,
  RouteParamsType,
  RoutePropsType
>;

type RoutedOwnProps = {|
  ...OwnProps,
  ...RouterProps,
|};

type LoadedByHashIdOwnProps = {|
  ...RoutedOwnProps,
  ...LoadByHashidInjectedProps<ProcedureType>,
|};

type LoadedVersionsOwnProps = {|
  ...LoadedByHashIdOwnProps,
  ...LoadVersionsInjectedProps<VersionType>,
|};

type StateProps = {|
  procedure: ProcedureType,
  fetchProcedure: () => Promise<any>,
  version: VersionType | null,
  editableContent: ProcedureContentType,
  versions: Array<VersionType>,
  versionId?: string,
  advancedModeEnabled: boolean,
  procedureAndUserInSameAccount: boolean,
  fetchProcedureVersions: () => Promise<any>,
  mediaURL: string,
  mediaURLRead: string,
  closedSideDiagram: boolean,
  versionComments: Array<CommentType>,
  userId?: number,
  deletingDocument: boolean,
  ...AcceptanceProps,
|};

type DispatchProps = {|
  loadComments: (
    objectId: number,
    contentType: FilterContentType
  ) => Promise<any>,
  loadAcceptance: AsyncAction<typeof loadAcceptance, Promise<any>>,
  setSideDiagramState: (procedureId: number, closed: boolean) => void,
  toggleAdvancedMode: () => void,
  showNotification: (message: string, level?: string) => void,
  setAcceptanceVersionViewed: AsyncAction<
    typeof setAcceptanceVersionViewed,
    Promise<any>
  >,
|};

type ConnectedProps = {|
  ...LoadedVersionsOwnProps,
  ...StateProps,
  ...DispatchProps,
|};

type Props = {|
  ...ConnectedProps,
  ...ModelSubscribedComponentPropsType,
  printing: boolean,
|};

type State = {
  collapsedSteps: Array<number>,
  showVersionsSidebar: boolean,
  highlightVersionChanges: boolean,
  showOverlayDiagram: boolean,
};

class ProcedureDetailPage extends React.Component<Props, State> {
  mounted: boolean;

  constructor(props: Props) {
    super(props);

    this.state = {
      highlightVersionChanges: true,
      collapsedSteps: [],
      showVersionsSidebar: !!props.versionId,
      showOverlayDiagram: false,
    };

    this.props.onModelChange(this.handleModelChange);
  }

  componentDidMount() {
    const {procedure, loadComments, loadAcceptance} = this.props;
    this.mounted = true;
    loadComments(procedure.id, 'procedure');
    loadAcceptance(procedure.id, 'procedure');

    if (window.location.hash) {
      // Chrome doesn't scroll into step work without this 250ms interval
      setTimeout(
        () => this.scrollIntoStep(window.location.hash.replace('#', '')),
        250
      );
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  handleModelChange = (message: ModelChangeMessageType) => {
    const {procedure, version, deletingDocument} = this.props;
    const expectedRelated = {
      content_type: 'procedure',
      id: procedure.id,
    };

    if (deletingDocument) {
      return;
    }

    if (procedure != null && procedure.id != null) {
      runWhenAny(
        message,
        [
          modelIdMatch('procedure', procedure.id),
          allRelatedMatch('procedureversion', expectedRelated),
          allRelatedMatch('review', expectedRelated),
        ],
        () => this.ensureProcedureIsLoaded()
      );

      runWhenAny(message, [allRelatedMatch('comment', expectedRelated)], () =>
        this.props.loadComments(procedure.id, 'procedure')
      );
    }

    if (version != null) {
      const expectedRelatedVersion = {
        content_type: 'procedureversion',
        id: version.id,
      };

      runWhenAny(
        message,
        [allRelatedMatch('acceptance', expectedRelatedVersion)],
        () => this.props.loadAcceptance(procedure.id, 'procedure')
      );
    }
  };

  ensureProcedureIsLoaded = whenVisible((): Promise<any> => {
    this.props.refetchLanes();
    return this.props.fetchProcedure().then(
      () => this.props.fetchProcedureVersions(),
      () => {
        // only 404 errors will reach this code
        this.props.showNotification(gt('Procedure not found'), 'error');
        this.props.router.goBack();
      }
    );
  });

  toggleDiagramOverlay = () => {
    this.setState(({showOverlayDiagram}) => ({
      showOverlayDiagram: !showOverlayDiagram,
    }));
  };

  toggleSidebarDiagram = () => {
    const {setSideDiagramState, procedure, closedSideDiagram} = this.props;
    setSideDiagramState(procedure.id, !closedSideDiagram);
  };

  scrollIntoStep = (stepId: string) => {
    const stepNode = document.getElementById(stepId);
    const currentStepHash = window.location.hash.replace('#', '');

    if (stepNode != null) {
      if (stepId === currentStepHash) {
        /* on page open */
        stepNode.scrollIntoView({behavior: 'smooth'});
      } else {
        /* on step link click */
        location.href = `#${stepId}`;
      }
    }
  };

  throttledShowNotification = throttle(
    (message: string) => {
      this.props.showNotification(message, 'info');
    },
    NOTIFICATION_DEFAULT_TIMEOUT,
    {leading: true, trailing: false}
  );

  render() {
    if (!this.props.procedure) {
      return null;
    }

    const {
      procedure,
      version,
      versions,
      router,
      versionId,
      route,
      procedureAndUserInSameAccount,
      advancedModeEnabled,
      editableContent,
      fetchProcedure,
      fetchProcedureVersions,
      toggleAdvancedMode,
      location,
      closedSideDiagram,
      versionComments,
      acceptance,
    } = this.props;

    const {showVersions, showComments, showAccept} = route.props;
    const orphans = getMemoizedProcedureOrphanSteps(
      editableContent.content,
      procedure.steps_blocked_at
    );
    const sidebarVisible =
      showVersions === true || showComments === true ? 'sidebar-visible' : '';

    const previousVersion =
      editableContent != null && versions != null && versionId != null
        ? formatPreviousProcedureVersion(
            getComparedVersion(
              editableContent.url,
              versions,
              location.query['selected-version-group']
            )
          ) || null
        : null;

    return (
      <CollapsedStepsState stepsIds={editableContent.content.map(({id}) => id)}>
        {({
          collapsedSteps,
          numCollapsedSteps,
          toggleAllStepsCollapse,
          toggleStepCollapse,
        }) => (
          <VersionDiffer
            location={location}
            content={editableContent}
            versions={versions}
            previousVersion={previousVersion}
            versionAvailable={version != null}
          >
            {(contentWithDiff, showingDiff, toggleDiff) => {
              const actionButtonsProps = {
                contentType: 'procedure',
                document: procedure,
                canChange: procedure.can_change,
                refreshData: this.ensureProcedureIsLoaded,
                additionalContent: (container) => (
                  <ProcedureHeaderOptionsDropdown
                    advancedModeEnabled={advancedModeEnabled}
                    toggleAdvancedMode={toggleAdvancedMode}
                    numCollapsedSteps={numCollapsedSteps}
                    onToggleAllStepsCollapse={toggleAllStepsCollapse}
                    stepsLength={contentWithDiff.content.length}
                    canEdit={procedure.can_edit}
                    hideDownloadOptions={Boolean(version)}
                    hideAdvancedMode={Boolean(version)}
                    overlaysContainer={container}
                  />
                ),
              };

              return (
                <SPPage
                  pageTitle={procedure.name}
                  breadcrumbProps={{
                    procedureId: procedure.id,
                    docName: getProcedureName(procedure.name),
                    link: procedure.html_url + router.location.search,
                    version: version,
                  }}
                  horizontalPadding={false}
                  actionArea={
                    version != null ? (
                      <GenericDocumentActionButtons
                        version={version}
                        isLastVersion={isLastVersion(version, versions)}
                        pageContext={'view-version'}
                        {...actionButtonsProps}
                      />
                    ) : (
                      <GenericDocumentActionButtons
                        version={version}
                        pageContext={'view-published'}
                        {...actionButtonsProps}
                      />
                    )
                  }
                  sidebar={
                    procedureAndUserInSameAccount ? (
                      <ProcedurePageSidebar
                        fetchProcedureVersions={fetchProcedureVersions}
                        versionId={versionId}
                        version={version}
                        userId={this.props.userId}
                        fetchProcedure={fetchProcedure}
                        procedure={procedure}
                        acceptance={this.props.acceptance}
                        versions={versions}
                        versionComments={versionComments}
                        route={route}
                        router={router}
                      />
                    ) : null
                  }
                  versionbar={
                    <>
                      {version ? (
                        <VersionInfoBar
                          showingDiff={showingDiff}
                          toggleDiff={toggleDiff}
                          objectTypeName={gt('procedure')}
                          version={version}
                          className={sidebarVisible}
                        />
                      ) : null}
                      {showAccept != true && acceptance != null ? (
                        <PendingAcceptanceInfoBar
                          document={procedure}
                          acceptance={acceptance}
                          location={location}
                          openedVersion={version}
                        />
                      ) : null}
                      {version == null ? (
                        <SignoffRequiredInfoBar
                          contentType="procedure"
                          objectId={procedure.id}
                        />
                      ) : null}
                    </>
                  }
                  alternativeOverlayContent={
                    this.state.showOverlayDiagram ? (
                      <ProcedureDiagramMobile
                        toggleDiagram={this.toggleDiagramOverlay}
                        steps={editableContent.content}
                      />
                    ) : null
                  }
                >
                  <DocumentPresenceMembers
                    contentType="procedure"
                    objectId={this.props.procedure.id}
                    containerClass={cs('col-12', {
                      'col-md-8': !closedSideDiagram,
                    })}
                    pageContext="view"
                    contexts={['view', 'edit', 'task']}
                  />
                  <VideoManagerProvider>
                    <ViewProcedureMetaData
                      content={contentWithDiff}
                      procedure={procedure}
                      fetchProcedure={fetchProcedure}
                      procedureAndUserInSameAccount={
                        procedureAndUserInSameAccount
                      }
                      warningComponent={
                        <Fragment>
                          {showNoApprovedVersionsAlertBoxIfNotApproved(
                            'procedure',
                            procedure.approved_at
                          )}
                          <LargeContentAlertBox
                            contentType="procedure"
                            id={procedure.id}
                            isLarge={
                              contentWithDiff && contentWithDiff.is_large
                            }
                          />
                          <ReviewReminderWarning
                            contentType="procedure"
                            versionId={procedure.current_version.id}
                            versionContentType="procedureversion"
                            canReview={procedure.can_edit}
                            onReview={fetchProcedure}
                            reviewDate={procedure.next_review_at}
                          />
                        </Fragment>
                      }
                      advancedModeEnabled={advancedModeEnabled}
                      mediaURL={this.props.mediaURL}
                      mediaURLRead={this.props.mediaURLRead}
                      containerAdditionalClass={sidebarVisible}
                    />
                    <StyledStepsListDIV className="js-document-body container pt-0">
                      <div className="row">
                        <div
                          className={cs(
                            'pt-4',
                            !closedSideDiagram && !this.props.printing
                              ? 'col-md-8'
                              : 'col-12'
                          )}
                        >
                          <ViewStepsModeTitle
                            numCollapsedSteps={numCollapsedSteps}
                            toggleAllStepsCollapse={toggleAllStepsCollapse}
                          />
                          <ol className="steps">
                            {contentWithDiff.content.map((step, index) => (
                              <li key={index} id={step.id}>
                                <ProcedureStep
                                  orphan={orphans.has(step.id)}
                                  disableCheckbox={true}
                                  index={index}
                                  step={step}
                                  showCopyLink={version == null}
                                  hideCheckbox={!!version}
                                  collapsed={collapsedSteps[step.id]}
                                  mediaURL={this.props.mediaURL}
                                  mediaURLRead={this.props.mediaURLRead}
                                  onToggleCollapse={toggleStepCollapse}
                                  actionButtons={
                                    version == null &&
                                    (!step.type || step.type === 'content') ? (
                                      <AttachTooltip
                                        placement="left"
                                        tooltipContent={gt(
                                          'Start this procedure to begin checking off steps.'
                                        )}
                                      >
                                        {(handlers) => (
                                          <span
                                            className={`${
                                              isRightToLeftWrapper(
                                                step.title || ''
                                              )
                                                ? 'float-left'
                                                : 'float-right'
                                            } d-inline-block`}
                                            {...handlers}
                                          >
                                            <StyledCheckboxButton
                                              disabled={true}
                                              isChecked={false}
                                              onClick={() => null}
                                              fixedWidth={true}
                                            />
                                          </span>
                                        )}
                                      </AttachTooltip>
                                    ) : null
                                  }
                                  fieldInputs={
                                    <StepViewFieldWrapper
                                      fields={step.fields}
                                      showNotification={
                                        this.throttledShowNotification
                                      }
                                    />
                                  }
                                  choicesList={
                                    step.type === 'decision' ? (
                                      <ViewableChoices
                                        choices={step.choices}
                                        steps={editableContent.content}
                                        edit={
                                          sidebarVisible === 'sidebar-visible'
                                        }
                                      />
                                    ) : null
                                  }
                                  next={
                                    <StepFooter
                                      step={step}
                                      stepLaneId={
                                        editableContent.lanes[step.id]
                                      }
                                      laneDiff={
                                        contentWithDiff.lanesDiff != null
                                          ? contentWithDiff.lanesDiff[step.id]
                                          : undefined
                                      }
                                      steps={editableContent.content}
                                    />
                                  }
                                />
                              </li>
                            ))}
                          </ol>

                          {contentWithDiff.content.length === 0 ? (
                            <ListMessage gray={true}>
                              {gt('No steps for this procedure')}
                            </ListMessage>
                          ) : null}

                          {version == null ? (
                            <SignoffForm
                              objectId={procedure.id}
                              contentType="procedure"
                              versionId={procedure.current_version.id}
                              versionContentType="procedureversion"
                              objectName={procedure.name}
                            />
                          ) : null}
                        </div>

                        {!this.state.showOverlayDiagram ? (
                          <ProcedureDiagramSidebar
                            className="col-md-4 d-print-none"
                            onStepClick={this.scrollIntoStep}
                            showingSidebarDiagram={!closedSideDiagram}
                            toggleSidebarDiagram={this.toggleSidebarDiagram}
                            toggleOverlayDiagram={this.toggleDiagramOverlay}
                            versionId={procedure.current_version.id}
                            versionContentType="procedureversion"
                            steps={editableContent.content}
                          />
                        ) : null}
                      </div>
                    </StyledStepsListDIV>
                  </VideoManagerProvider>
                  <ToggleProcedureDiagramMobileButton
                    showingDiagram={false}
                    toggleDiagram={this.toggleDiagramOverlay}
                  />
                  {version == null ? (
                    <ProcedureDetailTour
                      additionalData={contentWithDiff.content.length}
                    />
                  ) : null}
                </SPPage>
              );
            }}
          </VersionDiffer>
        )}
      </CollapsedStepsState>
    );
  }
}

const mapStateToProps = (
  state,
  {
    params,
    fetchedByHashid: {object, fetchObject},
    fetchedVersions: {fetchVersions, objectVersion, objectVersions},
  }
): StateProps => {
  const user = CurrentUserSelector(state).data;
  const settings = accountSettingsSelector(state).data;

  return {
    procedure: object,
    fetchProcedure: fetchObject,
    version: objectVersion ? objectVersion : null,
    editableContent: getContentFromProcedure(objectVersion || object),
    fetchProcedureVersions: fetchVersions,
    versionId: params.versionid,
    versions: objectVersions ? objectVersions : [],
    procedureAndUserInSameAccount:
      object !== undefined ? settings.account_id === object.account_id : false,
    advancedModeEnabled: objectVersion
      ? false
      : // $FlowFixMe
        state.proceduresPage.get('procedureAdvancedMode'),
    mediaURL: settings.MEDIA_URL,
    mediaURLRead: settings.MEDIA_URL_READ,
    closedSideDiagram: state.proceduresPage.getIn(
      ['closedSideDiagrams', object.id.toString()],
      !user.is_diagrams_expanded
    ),
    versionComments: getRelatedVersionComments(
      state,
      'procedure',
      object,
      'procedureversion',
      params.versionid
    ),
    userId: user.id,
    deletingDocument: state.toasts.isDeletingDocument,
    ...getAcceptanceData<ProcedureType>(state, 'procedure', object),
  };
};
const mapDispatchToProps: DispatchProps = {
  // $FlowFixMe
  toggleAdvancedMode: toggleProcedureAdvancedMode,
  // $FlowFixMe
  showNotification,
  // $FlowFixMe
  loadAcceptance,
  // $FlowFixMe
  setAcceptanceVersionViewed,
  // $FlowFixMe
  loadComments,
  // $FlowFixMe
  setSideDiagramState,
};

export default withTypedRouter<OwnProps, RouterProps>(
  loadProcedureByHashid<RoutedOwnProps, ProcedureType>(
    loadProcedureVersionsByObjectid<LoadedByHashIdOwnProps, VersionType>(
      connect<
        ConnectedProps,
        LoadedVersionsOwnProps,
        StateProps,
        DispatchProps,
        StoreShapeType,
        _
      >(
        mapStateToProps,
        mapDispatchToProps
      )(
        keepUpdatedEnhancer<ConnectedProps>(
          detectPrintHOC(
            fetchLanesForDocument(
              ProcedureDetailPage,
              'procedure',
              (props) => props.procedure.id
            )
          )
        )
      )
    )
  )
);

type BreadcrumbPropsOwnProps = {|
  procedureId: number,
  containerProcess?: ProcessType,
  active: boolean,
  icon: string,
|};

type RoutedBreadcrumbPropsOwnProps = {|
  ...BreadcrumbPropsOwnProps,
  ...RouterProps,
|};

type BreadcrumbStateProps = {|
  containerProcess: ProcessType | void,
|};

type BreadcrumbProps = {|
  dispatch: any,
  ...RoutedBreadcrumbPropsOwnProps,
  ...BreadcrumbStateProps,
|};

export const BaseMaybeProcedureProcessContainerBreadcrumb = ({
  containerProcess,
  active,
  icon,
}: BreadcrumbProps) =>
  containerProcess != null ? (
    <BreadcrumbItemStructure
      shrinkText={true}
      active={active}
      icon={icon}
      text={containerProcess.name}
      link={containerProcess.html_url}
    />
  ) : null;

const breadcrumbMapStateToProps = (state, props): BreadcrumbStateProps => ({
  containerProcess: getProcessFromParams(state, {
    location: props.location,
    procedureId: props.procedureId,
  }),
});

export const MaybeProcedureProcessContainerBreadcrumb = withTypedRouter<
  BreadcrumbProps,
  RouterProps
>(
  connect<
    BreadcrumbProps,
    RoutedBreadcrumbPropsOwnProps,
    BreadcrumbStateProps,
    _,
    StoreShapeType,
    _
  >(
    breadcrumbMapStateToProps,
    null
  )(BaseMaybeProcedureProcessContainerBreadcrumb)
);
